import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import {
  MagnifyingGlass, Gauge, ClipboardText, Receipt, Package, Factory, Cube,
  UsersThree, Storefront, ChartBar, Database, UserList, ClockCounterClockwise,
  Truck, KeyReturn, ArrowUp, ArrowDown,
} from "@phosphor-icons/react";

// Static quick-navigation commands (manager scope).
const NAV_COMMANDS = [
  { id: "n-dash", label: "Dashboard", icon: Gauge, to: "/", keywords: "home overview" },
  { id: "n-jobs", label: "Jobs", icon: ClipboardText, to: "/jobs", keywords: "job orders" },
  { id: "n-newpo", label: "New Purchase Order", icon: Receipt, to: "/paper-orders?new=1", keywords: "create po paper buy" },
  { id: "n-po", label: "Paper Orders", icon: Receipt, to: "/paper-orders", keywords: "purchase paper po" },
  { id: "n-prod", label: "Production Entry", icon: Factory, to: "/production/board", keywords: "board printing gluing stitching" },
  { id: "n-disp", label: "Dispatch Slips", icon: Package, to: "/dispatch-slips", keywords: "loading dispatch" },
  { id: "n-items", label: "Items", icon: Cube, to: "/items", keywords: "master product" },
  { id: "n-parties", label: "Parties", icon: UsersThree, to: "/parties", keywords: "customer master" },
  { id: "n-suppliers", label: "Suppliers", icon: Storefront, to: "/suppliers", keywords: "vendor master" },
  { id: "n-transport", label: "Transport (Vehicles / Drivers)", icon: Truck, to: "/transport", keywords: "vehicle driver type" },
  { id: "n-reports", label: "Analytics & Reports", icon: ChartBar, to: "/reports/analytics", keywords: "report speed party freight" },
  { id: "n-plkg", label: "P/L per Kg", icon: ChartBar, to: "/reports/pl-per-kg", keywords: "margin profit band" },
  { id: "n-alerts", label: "Alerts", icon: ChartBar, to: "/reports/alerts", keywords: "reconciliation slow" },
  { id: "n-company", label: "Company Profile & Settings", icon: Database, to: "/company", keywords: "settings pl bands logo" },
  { id: "n-users", label: "Users", icon: UserList, to: "/users", keywords: "access staff login" },
  { id: "n-audit", label: "Activity Log", icon: ClockCounterClockwise, to: "/activity-log", keywords: "audit history" },
];

const TYPE_META = {
  job: { icon: ClipboardText, tag: "Job" },
  party: { icon: UsersThree, tag: "Party" },
  item: { icon: Cube, tag: "Item" },
  supplier: { icon: Storefront, tag: "Supplier" },
  po: { icon: Receipt, tag: "PO" },
};

export default function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Global hotkey (Cmd/Ctrl + K) and "/" when not typing.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) { setQ(""); setRemote([]); setActive(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  // Debounced remote search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setRemote([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(term)}`)
        .then((r) => setRemote(r.data.results || []))
        .catch(() => setRemote([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const term = q.trim().toLowerCase();
  const navMatches = term
    ? NAV_COMMANDS.filter((c) => (c.label + " " + c.keywords).toLowerCase().includes(term))
    : NAV_COMMANDS;

  // Flatten to a single list for keyboard nav: nav commands first, then remote records.
  const flat = [
    ...navMatches.map((c) => ({ kind: "nav", ...c })),
    ...remote.map((r, i) => ({ kind: "record", id: `r-${i}-${r.id}`, ...r })),
  ];

  const go = useCallback((entry) => {
    if (!entry) return;
    setOpen(false);
    nav(entry.to);
  }, [nav]);

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  useEffect(() => { setActive(0); }, [q, remote.length]);

  return (
    <>
      <button
        data-testid="command-palette-trigger"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-[var(--text-3)] bg-white border border-[var(--border)] hover:border-[var(--text-3)] transition-colors min-w-[130px] md:min-w-[220px]"
      >
        <MagnifyingGlass size={16} weight="bold" />
        <span className="hidden md:inline flex-1 text-left">Search…</span>
        <kbd className="hidden md:inline text-[10px] font-semibold bg-zinc-100 border border-[var(--border)] rounded px-1.5 py-0.5">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4" data-testid="command-palette">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-xl bg-white rounded-md shadow-2xl border border-[var(--border)] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 px-4 border-b border-[var(--border)]">
              <MagnifyingGlass size={18} weight="bold" className="text-[var(--text-3)]" />
              <input
                ref={inputRef}
                data-testid="command-palette-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Search jobs, POs, parties, items… or jump to a page"
                className="flex-1 py-4 text-sm outline-none bg-transparent"
              />
              {loading && <div className="w-4 h-4 border-2 border-zinc-300 border-t-[var(--text-1)] rounded-full animate-spin" />}
            </div>

            <div className="max-h-[52vh] overflow-y-auto py-2">
              {flat.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-[var(--text-3)]">No matches for “{q}”.</div>
              ) : (
                <>
                  {navMatches.length > 0 && (
                    <div className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-[var(--text-3)]">Go to</div>
                  )}
                  {navMatches.map((c, i) => {
                    const idx = i;
                    return <Row key={c.id} idx={idx} active={active} setActive={setActive} onClick={() => go(c)} icon={c.icon} label={c.label} />;
                  })}
                  {remote.length > 0 && (
                    <div className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-[var(--text-3)]">Records</div>
                  )}
                  {remote.map((r, i) => {
                    const idx = navMatches.length + i;
                    const meta = TYPE_META[r.type] || {};
                    return (
                      <Row key={`r-${i}`} idx={idx} active={active} setActive={setActive} onClick={() => go(r)}
                        icon={meta.icon || MagnifyingGlass} label={r.label} sub={r.sublabel} tag={meta.tag} />
                    );
                  })}
                </>
              )}
            </div>

            <div className="flex items-center gap-4 px-4 py-2 border-t border-[var(--border)] bg-zinc-50 text-[10px] text-[var(--text-3)]">
              <span className="flex items-center gap-1"><ArrowUp size={11} /><ArrowDown size={11} /> navigate</span>
              <span className="flex items-center gap-1"><KeyReturn size={11} /> open</span>
              <span className="ml-auto">esc to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ idx, active, setActive, onClick, icon: Icon, label, sub, tag }) {
  const isActive = idx === active;
  return (
    <button
      data-testid={`cmd-row-${idx}`}
      onMouseEnter={() => setActive(idx)}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${isActive ? "bg-[var(--text-1)] text-white" : "text-[var(--text-1)] hover:bg-zinc-50"}`}
    >
      <Icon size={16} weight="bold" className={isActive ? "text-white" : "text-[var(--text-3)]"} />
      <span className="flex-1 truncate">{label}</span>
      {sub && <span className={`text-xs truncate max-w-[45%] ${isActive ? "text-white/70" : "text-[var(--text-3)]"}`}>{sub}</span>}
      {tag && <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isActive ? "bg-white/20" : "bg-zinc-100 text-[var(--text-3)]"}`}>{tag}</span>}
    </button>
  );
}
